import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { ArrowLeft, FileText } from 'lucide-react';
import { CurriculumTimeline, cn, MathText } from '@jeraisonne/ui';
import { currentPackIndex, levelLabel, sortPacks } from '@jeraisonne/db/curriculum';
import { useCurriculumTimeline, useMyProgression } from '@/lib/curriculum/api';
import { usePackTestsState } from '@/lib/pack-tests';
import { PackTestButton } from '@/features/pack-test/pack-test-button';
import {
  progressionSubjects,
  toTimelineFromProgression,
  yearSummary,
} from '@/lib/curriculum/progress';

/**
 * Route élève `/student/progression` — « Mon année » (lot F, migration 0289) :
 * la frise de la progression type de son niveau (une par matière / spécialité),
 * le pack du moment mis en avant, et pour chaque pack le % de cartes maîtrisées
 * (seuil 80) et les HORA cumulés. Toucher un pack affiche ses SKILLCARDS ;
 * « Se tester » (payant, migration 0336) lance un test sur ses cartes.
 */
export function StudentProgressionPage() {
  const progression = useMyProgression();
  const level = progression.data?.level ?? null;
  const timeline = useCurriculumTimeline(level, null);
  const packs = progression.data?.packs ?? [];
  const subjects = progressionSubjects(packs);
  const [subject, setSubject] = useState<string | null>(null);
  const active = subject ?? subjects[0]?.value ?? null;
  const list = sortPacks(packs.filter((p) => p.subject === active));
  const summary = yearSummary(packs, active);
  const [openId, setOpenId] = useState<string | null>(null);
  const open = (timeline.data ?? []).find((p) => p.id === openId) ?? null;
  const tests = usePackTestsState(list.map((p) => p.id));

  return (
    <div className="mx-auto max-w-5xl space-y-5 px-4 py-6 pb-[max(env(safe-area-inset-bottom),1.5rem)] sm:py-8">
      <div>
        <Link
          to="/student/dashboard"
          className="text-ink-500 hover:text-ink-800 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm"
        >
          <ArrowLeft className="h-4 w-4" /> Accueil
        </Link>
        <h1 className="font-display text-ink-900 text-2xl font-extrabold">
          Mon année{level ? ` · ${levelLabel(level)}` : ''}
        </h1>
        <p className="text-ink-500 text-sm">
          La progression type JeRaisonne de ton niveau. Ton prof peut suivre un ordre différent :
          l’important est de faire monter la maîtrise de chaque SKILLCARD.
        </p>
      </div>

      {progression.error && (
        <p role="alert" className="text-accent-600 text-sm">
          Impossible de charger ta progression. Réessaie dans un instant.
        </p>
      )}

      {progression.data && !level && (
        <p className="text-ink-600 text-sm">
          Indique ton niveau dans ton profil pour voir la progression de ton année.
        </p>
      )}

      {progression.data && level && packs.length === 0 && (
        <p className="text-ink-600 text-sm">
          La progression de ton niveau arrive bientôt. En attendant, retrouve tes cartes dans{' '}
          <Link to="/student/skillcards" className="text-primary-600 font-semibold underline">
            Mes SKILLCARDS
          </Link>
          .
        </p>
      )}

      {subjects.length > 1 && (
        <div role="tablist" aria-label="Matière" className="bg-ink-100 flex gap-1 rounded-xl p-1">
          {subjects.map((s) => (
            <button
              key={s.value}
              type="button"
              role="tab"
              aria-selected={active === s.value}
              onClick={() => setSubject(s.value)}
              className={cn(
                'min-h-[2.75rem] flex-1 rounded-lg px-2 text-sm font-semibold',
                active === s.value ? 'text-ink-900 bg-white shadow-sm' : 'text-ink-500',
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {list.length > 0 && (
        <>
          <p className="text-ink-700 text-sm" data-testid="year-summary">
            <strong className="text-ink-900">{summary.percent ?? 0} %</strong> des SKILLCARDS de
            l’année maîtrisées · <strong className="text-ink-900">{summary.hora}</strong> HORA
          </p>
          <CurriculumTimeline
            packs={list.map(toTimelineFromProgression)}
            currentIndex={currentPackIndex(list)}
            orientation="vertical"
            renderLink={(pack, children, className) => (
              <div className={cn(className, 'space-y-3')}>
                <button
                  type="button"
                  aria-expanded={openId === pack.id}
                  onClick={() => setOpenId((cur) => (cur === pack.id ? null : pack.id))}
                  className="block w-full text-left"
                >
                  {children}
                </button>
                <PackTestButton packId={pack.id} state={tests.data?.[pack.id]} />
              </div>
            )}
          />
        </>
      )}

      {open && (
        <section
          className="border-ink-200 space-y-2 rounded-2xl border bg-white p-4"
          aria-label={open.title}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-display text-ink-900 text-base font-bold">{open.title}</h2>
            {/* Fiche de révision du pack (0723) : règles et pièges compilés, à lire ou à imprimer. */}
            {open.cards.length > 0 && (
              <Link
                to="/pack/$packId/fiche"
                params={{ packId: open.id }}
                search={{ student: undefined }}
                className="text-primary-700 hover:text-primary-800 inline-flex min-h-[2.75rem] items-center gap-1.5 text-sm font-semibold"
              >
                <FileText className="h-4 w-4" aria-hidden /> Fiche de révision
              </Link>
            )}
          </div>
          {open.cards.length === 0 ? (
            <p className="text-ink-500 text-sm">Les SKILLCARDS de ce pack arrivent bientôt.</p>
          ) : (
            <ul className="divide-ink-100 divide-y">
              {open.cards.map((c) => (
                <li key={c.id}>
                  <Link
                    to="/card/$cardId"
                    params={{ cardId: c.id }}
                    className="text-ink-800 hover:text-primary-700 flex min-h-[2.75rem] items-center text-sm font-medium"
                  >
                    <MathText>{c.title}</MathText>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}
